(ns ui.view.nev.sub
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entities.view :as view-schema]
   [common.schema.view.single-entity :as sevs]
   [common.util :as cu]
   [oops.core :as oops]
   [re-frame.core :as rf]

   ;; TODO: remove this
   [ui.state.session.sub :as session-sub]
   [ui.view.nev.model :as model]
   [common.schema.entity-types :as entity-types]))

;; Antipattern, but it's a workaround for the fact that re-frame doesn't support
;; dynamic sub-tree subscriptions
(rf/reg-sub
 ::state
 (fn [db [_ {:keys [state-path] :as _ctx}]]
   (get-in db state-path)))

(rf/reg-sub
 ::initialized?
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/initialized-path)))

(rf/reg-sub
 ::card-remount-counter
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/card-remount-counter-path 1)))

(rf/reg-sub
 ::sidebar-mode
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/sidebar-mode-path)))

(rf/reg-sub
 ::cached-entities
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state] _]
   (get-in state model/entities-path)))

(rf/reg-sub
 ::cached-entities-by-type
 (fn [[_ ctx]]
   [(rf/subscribe [::cached-entities ctx])])
 (fn [[cached-entities-by-id]]
   (group-by :entity/type (vals cached-entities-by-id))))

(rf/reg-sub
 ::view-id
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/view-id-path)))

(rf/reg-sub
 ::view-option
 (fn [[_ ctx]]
   [(rf/subscribe [::view-id ctx])
    (rf/subscribe [::view-options ctx])])
 (fn [[selected-value options] _]
   (->> options
        (cu/filter-by :value selected-value)
        (first))))

(rf/reg-sub
 ::selected-top-level-section
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/selected-top-level-section-path)))

(rf/reg-sub
 ::selected-section
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/selected-section-path)))

(rf/reg-sub
 ::selected-entity-id
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/selected-entity-id-path)))

(rf/reg-sub
 ::selected-entity-flagged?
 (fn [[_ ctx]]
   [(rf/subscribe [::selected-entity-id ctx])
    (rf/subscribe [::session-sub/flagged-entities])])
 (fn [[selected-entity-id flagged-entities]]
   (contains? flagged-entities selected-entity-id)))

(rf/reg-sub
 ::primary-entity-id
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/primary-entity-id-path)))

(rf/reg-sub
 ::selected-entity-type
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (get-in state model/selected-entity-type-path)))

(rf/reg-sub
 ::cached-view
 (fn [[_ ctx]]
   [(rf/subscribe [::view-id ctx])
    (rf/subscribe [::cached-entities ctx])])
 (fn [[view-id entities]]
   (get entities view-id)))

(rf/reg-sub
 ::cached-view-configuration
 (fn [[_ ctx]]
   [(rf/subscribe [::cached-view ctx])])
 (fn [[view-entity]]
   (:view/configuration view-entity)))

(rf/reg-sub
 ::original-view
 (fn [[_ ctx]]
   [(rf/subscribe [::view-id ctx])
    (rf/subscribe [::session-sub/entities])])
 (fn [[view-id global-entities-by-id]]
   (get global-entities-by-id view-id)))

(rf/reg-sub
 ::view-entity-type
 (fn [[_ ctx]]
   [(rf/subscribe [::cached-view ctx])])
 (fn [[view]]
   (get view :view/entity-type)))

(rf/reg-sub
 ::primary-entity-type
 (fn [[_ ctx]]
   [(rf/subscribe [::cached-view ctx])])
 (fn [[view-entity]]
   (get view-entity :view/entity-type)))

(rf/reg-sub
 ::view-options
 (fn [[_ ctx]]
   [(rf/subscribe [::cached-view ctx])
    (rf/subscribe [::session-sub/entities-for-type :entity.type/view])
    (rf/subscribe [::view-entity-type ctx])])
 (fn [[cached-view all-views view-entity-type]]
   (->> all-views
        (filterv (fn [{:keys [db/id view/entity-type view/type]}]
                   (and (not= id (:db/id cached-view))
                        (= entity-type view-entity-type)
                        (= type :view.type/single-entity))))
        (cons cached-view)
        (mapv (fn [view]
                {:value (:db/id view)
                 :label (:view/name view)}))
        (sort-by :label))))

(rf/reg-sub
 ::card-states
 (fn [[_ ctx]]
   [(rf/subscribe [::state ctx])])
 (fn [[state]]
   (->> (get-in state model/card-states-path)
        (mapcat (fn [[entity-id card-states-by-card-id]]
                  (->> card-states-by-card-id
                       (mapv (fn [[card-id card-state]]
                               [[entity-id card-id] card-state])))))
        (into {}))))

(defn merge-entity-maps
  "Merges multiple ID-indexed entity maps,
   giving priority to the entity with the latest `:common/updated-on` timestamp."
  [& maps]
  (->> maps
       (apply merge-with (fn [a b]
                           (if (> (:common/updated-on b) (:common/updated-on a))
                             b
                             a)))))

(rf/reg-sub
 ::entities-from-card-states
 (fn [[_ ctx]]
   [(rf/subscribe [::card-states ctx])])
 (fn [[card-states] _]
   (->> card-states
        (map (fn [[_ card-state]]
               (:entities card-state)))
        (apply merge-entity-maps))))

(rf/reg-sub
 ::all-cached-entities
 (fn [[_ ctx]]
   [(rf/subscribe [::cached-entities ctx])
    (rf/subscribe [::entities-from-card-states ctx])])
 (fn [[cached-entities entities-from-cards]]
   (merge-entity-maps cached-entities entities-from-cards)))

(rf/reg-sub
 ::primary-entity
 (fn [[_ ctx]]
   [(rf/subscribe [::primary-entity-id ctx])
    (rf/subscribe [::all-cached-entities ctx])])
 (fn [[entity-id entities-by-id]]
   (get entities-by-id entity-id)))

(rf/reg-sub
 ::selected-entity
 (fn [[_ ctx]]
   [(rf/subscribe [::selected-entity-id ctx])
    (rf/subscribe [::all-cached-entities ctx])])
 (fn [[selected-entity-id all-cached-entities]]
   (some->> selected-entity-id
            (get all-cached-entities))))

(rf/reg-sub
 ::entity-label
 (fn [[_ ctx]]
   [(rf/subscribe [::selected-entity ctx])
    (rf/subscribe [::all-cached-entities ctx])])
 (fn [[entity
       cached-entities-by-id] _]
   (if entity
     (entity/display-string entity :short #(get cached-entities-by-id %))
     "")))

(defn entity-type->icon-class
  [entity-type]
  (str "fal " (-> {:entity/type entity-type}
                  entity/default-icon
                  cu/kw->str)))

(rf/reg-sub
 ::entity-icon-class
 (fn [[_ ctx]]
   [(rf/subscribe [::selected-entity-type ctx])])
 (fn [[entity-type] _]
   (entity-type->icon-class entity-type)))

(rf/reg-sub
 ::entity-type-label
 (fn [[_ ctx]]
   [(rf/subscribe [::selected-entity-type ctx])])
 (fn [[entity-type] _]
   (loc/tr entity-type)))

(rf/reg-sub
 ::entity-loading?
 (fn [[_ ctx]]
   [(rf/subscribe [::primary-entity ctx])])
 (fn [[entity] _]
   (not entity)))

(rf/reg-sub
 ::selected-section-cards
 (fn [[_ ctx]]
   [(rf/subscribe [::selected-top-level-section ctx])
    (rf/subscribe [::selected-section ctx])
    (rf/subscribe [::cached-view-configuration ctx])
    (rf/subscribe [::cached-entities ctx])])
 (fn [[selected-top-level-section
       selected-section
       view-configuration
       cached-entities-by-id]]
   (let [card-ids (get-in view-configuration
                          [:top-level-sections
                           selected-top-level-section
                           :sections
                           selected-section
                           :cards])
         cards (->> card-ids
                    (mapv #(get cached-entities-by-id %)))]
     cards)))

(rf/reg-sub
 ::can-edit-view?
 (fn [[_ ctx]]
   [(rf/subscribe [::original-view ctx])
    (rf/subscribe [::session-sub/account])
    (rf/subscribe [::session-sub/current-permissions])])
 (fn [[original-view-entity account current-permissions]]
   (view-schema/has-write-access? account current-permissions original-view-entity)))

(rf/reg-sub
 ::system-default-view?
 (fn [[_ ctx]]
   [(rf/subscribe [::original-view ctx])])
 (fn [[view]]
   (:view/system-default? view)))

(rf/reg-sub
 ::view-owner-label
 (fn [[_ ctx]]
   [(rf/subscribe [::original-view ctx])
    (rf/subscribe [::session-sub/tenant-users-by-id])])
 (fn [[view users-by-id]]
   (->> view
        :common/owner
        (get users-by-id)
        entity/display-string)))

(defn scroll-element-into-view
  [id]
  (when-let [el (oops/ocall js/document "getElementById" id)]
    (oops/ocall el "scrollIntoView" (clj->js {:behavior :smooth}))))

(rf/reg-sub
 ::sidebar-primary-entity-item
 (fn [[_ ctx]]
   [(rf/subscribe [::primary-entity-id ctx])
    (rf/subscribe [::primary-entity ctx])
    (rf/subscribe [::view-entity-type ctx])
    (rf/subscribe [::selected-entity-id ctx])
    (rf/subscribe [::cached-view-configuration ctx])
    (rf/subscribe [::cached-entities ctx])
    (rf/subscribe [::session-sub/session-context])])
 (fn [[primary-entity-id
       primary-entity
       view-entity-type
       selected-entity-id
       view-configuration
       entities-by-id
       session-context]]
   (let [top-level-section-id :primary-entity
         visible-sections (->> (sevs/available-sections session-context view-entity-type)
                               (filterv (fn [{:keys [id] :as _section}]
                                          (and (sevs/primary-entity-section? id)
                                               (-> view-configuration
                                                   (get-in [:top-level-sections top-level-section-id :sections id :hidden?])
                                                   not)))))
         items (->> visible-sections
                    (mapv (fn [section]
                            (let [section-id (:id section)
                                  section-title (:title section)
                                  section-icon-class (:icon-class section)
                                  section-config (get-in view-configuration [:top-level-sections top-level-section-id :sections section-id])
                                  ord (:ord section-config)
                                  card-ids (:cards section-config)
                                  cards (->> card-ids
                                             (mapv #(get entities-by-id %)))
                                  items (->> cards
                                             (map-indexed
                                              (fn [card-idx {:keys [db/id card/title]}]
                                                {:id       id
                                                 :title    title
                                                 ;; TODO: make the scrolling into view more robust
                                                 :on-click #(scroll-element-into-view (str id "-" card-idx))}))
                                             vec)
                                  access (sevs/section-access session-context section)
                                  no-access? (= access :no-access)
                                  available-in-nev-2024? (sevs/available-in-nev-2024? section-id)
                                  no-module-access? (= access :no-module-access)
                                  disabled? (or no-access?
                                                no-module-access?
                                                (not available-in-nev-2024?))]
                              {:id                     section-id
                               :ord                    ord
                               :title                  section-title
                               :items                  items
                               :icon-class             section-icon-class
                               :disabled?              disabled?
                               :no-module-access?      no-module-access?
                               :available-in-nev-2024? available-in-nev-2024?
                               :helper-text            (sevs/section-helper-text session-context section)
                               :expandable?            (->> items count (< 1))})))
                    (sort-by :ord)
                    vec)]
     {:id                   primary-entity-id
      :selected?            (= selected-entity-id primary-entity-id)
      :primary-entity?      true
      :kicker               (loc/tr view-entity-type)
      :title                (entity/display-string primary-entity)
      :top-level-section-id top-level-section-id
      :entity-type          view-entity-type
      :open-intra-nev?      true
      :items                items
      :icon-class           (entity-type->icon-class view-entity-type)})))

;; TODO: Split into smaller parts and get rid of three nested loops
(rf/reg-sub
 ::sidebar-linked-entity-items
 (fn [[_ ctx]]
   [(rf/subscribe [::primary-entity-id ctx])
    (rf/subscribe [::primary-entity ctx])
    (rf/subscribe [::primary-entity-type ctx])
    (rf/subscribe [::cached-view-configuration ctx])
    (rf/subscribe [::selected-entity-id ctx])
    (rf/subscribe [::cached-entities-by-type ctx])
    (rf/subscribe [::all-cached-entities ctx])
    (rf/subscribe [::session-sub/session-context])])
 (fn [[primary-entity-id
       primary-entity
       primary-entity-type
       view-configuration
       selected-entity-id
       entities-by-type
       entities-by-id
       session-context]]
   (let [visible-sections (->> (sevs/available-sections session-context primary-entity-type)
                               (filterv (fn [{:keys [id] :as _section}]
                                          (and (sevs/linked-entity-section? id)
                                               (-> view-configuration
                                                   (get-in [:top-level-sections id :hidden?])
                                                   not))))
                               (sort-by (fn [{:keys [id] :as _section}]
                                          (get-in view-configuration [:top-level-sections id :ord])))
                               vec)]
     (->> visible-sections
          (mapcat
           (fn [section]
             (let [top-level-section-id (:id section)
                   section-entity-types (:entity-types section)
                   kicker-text (sevs/kicker-text session-context section)
                   entities
                   (->> section-entity-types
                        (mapcat #(get entities-by-type %))
                        (filterv (partial sevs/passes-section-entity-filter?
                                          {:section-id        top-level-section-id
                                           :primary-entity    primary-entity
                                           :primary-entity-id primary-entity-id}))
                        (sort-by :entity/type)
                        (mapv (fn [entity]
                                (let [id (:db/id entity)
                                      possibly-fresher-version-of-entity (get entities-by-id id)]
                                  ;; HACK: way to pull latest updates from card states since there is realtime subs
                                  ;; Implement explicit sub for sidebar entities later
                                  (if (and possibly-fresher-version-of-entity
                                           (> (:common/updated-on possibly-fresher-version-of-entity)
                                              (:common/updated-on entity)))
                                    possibly-fresher-version-of-entity
                                    entity)))))
                   items
                   (->> entities
                        (mapv (fn [entity]
                                (let [entity-id (:db/id entity)
                                      entity-type (:entity/type entity)
                                      badge-type (sevs/badge-type (assoc session-context :target-entity entity) section)
                                      badge-label (sevs/badge-label (assoc session-context :target-entity entity) section)

                                      items
                                      (->> entity-type
                                           (sevs/available-sections session-context)
                                           (filterv (fn [{:keys [id] :as _section}]
                                                      (and
                                                       (sevs/primary-entity-section? id)
                                                       (-> view-configuration
                                                           (get-in [:top-level-sections
                                                                    top-level-section-id
                                                                    :sections
                                                                    id
                                                                    :hidden?])
                                                           not))))
                                           (sort-by (fn [{:keys [id]}]
                                                      (get-in view-configuration
                                                              [:top-level-sections
                                                               top-level-section-id
                                                               :sections
                                                               id
                                                               :ord])))
                                           (mapv (fn [section]
                                                   (let [section-id (:id section)
                                                         title (:title section)
                                                         icon-class (:icon-class section)
                                                         card-ids (get-in view-configuration
                                                                          [:top-level-sections
                                                                           top-level-section-id
                                                                           :sections
                                                                           section-id
                                                                           :cards])
                                                         cards (->> card-ids
                                                                    (mapv #(get entities-by-id %)))
                                                         items (->> cards
                                                                    (map-indexed
                                                                     (fn [card-idx {:keys [db/id card/title]}]
                                                                       {:id       id
                                                                        :title    title
                                                                        ;; TODO: make the scrolling into view more robust
                                                                        :on-click #(scroll-element-into-view (str id "-" card-idx))}))
                                                                    vec)
                                                         access (sevs/section-access session-context section)
                                                         no-access? (= access :no-access)
                                                         available-in-nev-2024? (sevs/available-in-nev-2024? section-id)
                                                         no-module-access? (= access :no-module-access)
                                                         disabled? (or no-access?
                                                                       no-module-access?
                                                                       (not available-in-nev-2024?))]
                                                     {:id                     section-id
                                                      :title                  title
                                                      :items                  items
                                                      :expandable?            (->> items count (< 1))
                                                      :icon-class             icon-class
                                                      :access                 access
                                                      :disabled?              disabled?
                                                      :helper-text            (sevs/section-helper-text session-context section)
                                                      :no-module-access?      no-module-access?
                                                      :available-in-nev-2024? available-in-nev-2024?}))))]
                                  {:id                   entity-id
                                   :kicker               kicker-text
                                   :title                (entity/display-string entity)
                                   :badge-type           badge-type
                                   :badge-label          badge-label
                                   :entity-type          entity-type
                                   :linked-entity?       true
                                   :top-level-section-id top-level-section-id
                                   :selected?            (= entity-id selected-entity-id)
                                   :open-intra-nev?      true
                                   :items                items
                                   :icon-class           (entity-type->icon-class entity-type)}))))]
               items)))
          vec))))

(rf/reg-sub
 ::favorite-view?
 (fn [[_ ctx]]
   [(rf/subscribe [::view-id ctx])
    (rf/subscribe [::view-entity-type ctx])
    (rf/subscribe [::session-sub/favorite-single-entity-views])])
 (fn [[view-id view-entity-type favorite-single-entity-views]]
   (-> favorite-single-entity-views
       (get view-entity-type)
       (= view-id))))

;; TODO: Move these to own namespace and make generic

(rf/reg-sub
 ::add-company-linked-entity-options
 :<- [::session-sub/dealflow?]
 :<- [::session-sub/fundraising?]
 :<- [::session-sub/portfolio?]
 :<- [::session-sub/sales-pipelines?]
 :<- [::session-sub/feature-access? :feature/asset-types-fof]
 (fn [[dealflow?
       fundraising?
       portfolio?
       sales-pipelines?
       fof-assets?]]
   (->> [(when dealflow?
           :entity.type/oppo)

         (when (or fundraising? portfolio?)
           :entity.type/investor)

         (when portfolio?
           :entity.type/holding)

         (when fof-assets?
           :entity.type/fof-asset)

         (when sales-pipelines?
           :entity.type/sale-agreement)]
        (remove nil?)
        (mapv (fn [entity-type]
                {:value      entity-type
                 :icon-class (str "fal " (some->> entity-type
                                                  (assoc {} :entity/type)
                                                  entity/default-icon
                                                  cu/kw->str))
                 :label      (loc/tr entity-type)})))))

(rf/reg-sub
 ::add-person-linked-entity-options
 :<- [::session-sub/fundraising?]
 :<- [::session-sub/portfolio?]
 :<- [::session-sub/sales-pipelines?]
 (fn [[fundraising? portfolio? sales-pipelines?]]
   (->> [(when (or fundraising? portfolio?)
           :entity.type/investor)

         (when sales-pipelines?
           :entity.type/sale-agreement)]
        (remove nil?)
        (mapv (fn [entity-type]
                {:value      entity-type
                 :icon-class (str "fal " (some->> entity-type
                                                  (assoc {} :entity/type)
                                                  entity/default-icon
                                                  cu/kw->str))
                 :label (loc/tr entity-type)})))))

(rf/reg-sub
 ::can-configure-views-for-selected-entity-type?
 (fn [[_ ctx]]
   [(rf/subscribe [::selected-entity-type ctx])])
 (fn [[selected-entity-type]]
   (and
    (not (some #{:entity.type/survey-profile} [selected-entity-type]))
    (not (entity-types/counterparty-diligence-entity-type? selected-entity-type)))))

(rf/reg-sub
 ::can-switch-to-classic-mode?
 (fn [[_ ctx]]
   [(rf/subscribe [::selected-entity-type ctx])])
 (fn [[selected-entity-type]]
   (not (entity-types/counterparty-diligence-entity-type? selected-entity-type))))
